import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { clear, del, get, set } from "idb-keyval";
import { ApiError } from "@/lib/http";
import { notify } from "@/components/feedback/toaster";

/** Retry transient failures only — never 4xx, at most twice. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  const status = error instanceof ApiError ? error.status : (error as { status?: number }).status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return failureCount < 2;
}

function messageFor(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong";
}

/**
 * One QueryClient for the app. Background query failures stay quiet (the screen
 * shows its own error state); mutation failures surface as a toast unless the
 * caller opts out with `meta: { silent: true }`.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.errorToast) notify(messageFor(error), "error");
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.silent) return;
      notify(messageFor(error), "error");
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Keep entries around long enough to be worth persisting between sessions.
      gcTime: 24 * 60 * 60_000,
      refetchOnWindowFocus: true,
      retry: shouldRetry,
    },
    mutations: { retry: false },
  },
});

/**
 * Persist the query cache to IndexedDB so a cold reload paints real data
 * immediately (then revalidates in the background). `buster` is bumped per
 * build so a deploy discards an incompatible cache. Sensitive one-shot values
 * (SCIM/API-key plaintext, AI test replies) are never cached under a stable key,
 * so this is safe.
 */
export const queryPersister = createAsyncStoragePersister({
  storage: {
    getItem: (k) => get(k),
    setItem: (k, v) => set(k, v),
    removeItem: (k) => del(k),
  },
  key: "udip.rq-cache",
  throttleTime: 1_000,
});

export const PERSIST_BUSTER = import.meta.env.VITE_BUILD_ID ?? "dev";

export function clearPersistedCache() {
  void clear();
}
